import React, { useState, useEffect } from 'react';
import {
  TextField,
  Button,
  Box,
  CircularProgress,
  Typography,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Tabs,
  Tab,
  Autocomplete,
  Chip,
  Paper,
  Divider,
  Alert,
  useTheme,
  Tooltip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ModalMAIQ from 'app/components/ModalMAIQ';
import { ConfirmationDialogMAIQ } from 'app/components/ReusableUIComponents/LowLevelComponents/ConfirmationDialogMAIQ';
import CopyToClipboard from 'app/components/CopyToClipboard';
import { Resource, ResourceType, Role } from '../../types';
import { formatToLocalTime } from 'utils/dateUtils';
import { useResource } from 'app/context/ResourceContext';
import { useRole } from 'app/context/RoleContext';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 2, px: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

interface ResourceDetailsModalProps {
  open: boolean;
  onClose: () => void;
  resource: Resource;
  onSuccess: () => void;
}

const ResourceDetailsModal: React.FC<ResourceDetailsModalProps> = ({
  open,
  onClose,
  resource,
  onSuccess,
}) => {
  const theme = useTheme();
  const { editResource, deleteResource, attachRolesAndScopes, getResourceRolesAndScopes } = useResource();
  const { roles, getAllRoles } = useRole();

  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string>('');
  const [openConfirm, setOpenConfirm] = useState(false);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [formData, setFormData] = useState<Resource>(resource);
  const [originalData, setOriginalData] = useState<Resource>(resource);
  const [tabValue, setTabValue] = useState(0);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [isEditingRoles, setIsEditingRoles] = useState(false);
  const [existingRoles, setExistingRoles] = useState<Role[]>([]);

  useEffect(() => {
    setFormData(resource);
    getAllRoles();
    loadResourceRolesAndScopes();
  }, [resource]);

  const loadResourceRolesAndScopes = async () => {
    const resourceId = resource._id || resource['_id'];
    if (!resourceId) return;

    try {
      // const data = await getResourceRolesAndScopes(resourceId);
      const validRoles = (resource.roles || []).filter(r => !!(r && r._id && (r.name && r.name.trim().length > 0)));
      setExistingRoles(validRoles);
      setSelectedRoles(validRoles.map(role => role._id as string));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load roles');
    }
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    if (name === 'type') {
      setFormData(prev => ({ ...prev, type: value as ResourceType }));
      return;
    }
    setFormData(prev => ({
      ...prev,
      [name]: name === 'status' ? value === 'true' : value,
    }));
  };

  const handleEditSubmit = async () => {
    setIsSubmitting(true);
    setError('');
    setOpenConfirm(false);

    const resourceId = resource._id || resource['_id'];
    if (!resourceId) {
      setError('Resource ID is missing');
      setIsSubmitting(false);
      return;
    }

    try {
      if (isEditingRoles) {
        const roleNames = selectedRoles.map(roleId => 
          roles.find(r => r._id === roleId)?.name || ''
        ).filter(name => name !== '');
        
        await attachRolesAndScopes(resourceId, roleNames, []);
      } else {
        const resourceType = (formData.type ?? 'PAGE') as ResourceType;
        if (resourceType === 'PAGE' && !formData.path?.trim()) {
          setError('Path is required for PAGE type');
          setIsSubmitting(false);
          return;
        }
        const payload: Partial<Resource> = {
          type: resourceType,
          name: formData.name,
          component: formData.component,
          status: formData.status,
        };
        if (resourceType === 'PAGE' && formData.path != null) {
          payload.path = formData.path;
        }
        // COMPONENT: do not send path in API payload
        await editResource(resourceId, payload);
      }
      setIsEditing(false);
      setIsEditingRoles(false);
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteResource = async () => {
    setIsDeleting(true);
    setError('');
    setOpenDeleteConfirm(false);

    const resourceId = resource._id || resource['_id'];
    if (!resourceId) {
      setError('Resource ID is missing');
      setIsDeleting(false);
      return;
    }

    try {
      await deleteResource(resourceId);
      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <ModalMAIQ 
      open={open} 
      onClose={onClose} 
      hideDefaultCloseButton={true}
    >
      <Paper
        elevation={0}
        sx={{
          width: 600,
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 0,
        }}
      >
        {/* Header - fixed */}
        <Box
          sx={{
            p: 2,
            backgroundColor: '#0F2A52',
            color: 'white',
            zIndex: 10,
            flexShrink: 0,
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
          >
            <Typography variant="h6" fontWeight="600">
              Resource Details
            </Typography>
            <Box>
              <Tooltip title="Edit Resource">
                <IconButton
                  onClick={() => {
                    setIsEditing(true);
                    setOriginalData(formData);
                    if (tabValue === 1) {
                      setIsEditingRoles(true);
                    } else {
                      setIsEditingRoles(false);
                    }
                  }}
                  sx={{ color: 'white', p: 0.5 }}
                  size="small"
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete Resource">
                <IconButton 
                  onClick={() => setOpenDeleteConfirm(true)} 
                  sx={{ color: 'white', p: 0.5, ml: 1 }}
                  disabled={isDeleting}
                  size="small"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Close">
                <IconButton
                  aria-label="close"
                  onClick={onClose}
                  sx={{
                    color: 'white',
                    p: 0.5,
                    ml: 1
                  }}
                  size="small"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          <Typography variant="subtitle2" sx={{ mt: 0.5, fontWeight: 400, fontSize: '0.8rem' }}>
            ID: {resource._id || resource['_id']}
            <CopyToClipboard text={String(resource._id || resource['_id'] || '')} size="small" />
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mx: 2, mt: 2 }}>
            {error}
          </Alert>
        )}

        <Tabs 
          value={tabValue} 
          onChange={(_, newValue) => setTabValue(newValue)} 
          sx={{ 
            borderBottom: 1, 
            borderColor: 'divider',
            "& .MuiTabs-indicator": {
              height: 4,
              backgroundColor: theme.palette.primary.main,
            },
            flexShrink: 0,
          }}
        >
          <Tooltip title="View and edit resource properties">
            <Tab 
              label="Details" 
              sx={{ 
                textTransform: 'none', 
                fontWeight: tabValue === 0 ? 600 : 400,
                fontSize: '0.9rem',
                px: 3,
                color: tabValue === 0 ? theme.palette.primary.main : '#666',
              }} 
            />
          </Tooltip>
          <Tooltip title="Manage roles assigned to this resource">
            <Tab 
              label="Roles" 
              sx={{ 
                textTransform: 'none', 
                fontWeight: tabValue === 1 ? 600 : 400,
                fontSize: '0.9rem',
                px: 3,
                color: tabValue === 1 ? theme.palette.primary.main : '#666',
              }} 
            />
          </Tooltip>
        </Tabs>

        {/* Scrollable content area */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            position: 'relative',
          }}
        >
          <TabPanel value={tabValue} index={0}>
            <Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Type *
                </Typography>
                {isEditing ? (
                  <FormControl fullWidth size="small" required disabled={isSubmitting}>
                    <Select
                      name="type"
                      value={(formData.type ?? 'PAGE') as ResourceType}
                      onChange={handleSelectChange}
                      displayEmpty
                      renderValue={(value) => {
                        const isPage = value === 'PAGE';
                        return (
                          <Chip
                            size="small"
                            label={isPage ? 'Page' : 'Component'}
                            sx={{
                              backgroundColor: isPage ? theme.palette.primary.main : theme.palette.secondary.main,
                              color: 'white',
                              '& .MuiChip-label': { color: 'white' },
                            }}
                          />
                        );
                      }}
                      inputProps={{ sx: { py: 1.5 } }}
                    >
                      <MenuItem value="PAGE">Page</MenuItem>
                      <MenuItem value="COMPONENT">Component</MenuItem>
                    </Select>
                  </FormControl>
                ) : (
                  <Chip
                    size="small"
                    label={(formData.type ?? 'PAGE') === 'PAGE' ? 'Page' : 'Component'}
                    sx={{
                      backgroundColor: (formData.type ?? 'PAGE') === 'PAGE' ? theme.palette.primary.main : theme.palette.secondary.main,
                      color: 'white',
                      '& .MuiChip-label': { color: 'white' },
                    }}
                  />
                )}
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Resource Name *
                </Typography>
                <TextField
                  fullWidth
                  name="name"
                  value={formData.name}
                  onChange={handleTextChange}
                  required
                  disabled={!isEditing || isSubmitting}
                  variant="outlined"
                  size="small"
                  inputProps={{
                    sx: { py: 1.5 }
                  }}
                />
              </Box>

              {(formData.type ?? 'PAGE') === 'PAGE' && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    Path *
                  </Typography>
                  <TextField
                    fullWidth
                    name="path"
                    value={formData.path ?? ''}
                    onChange={handleTextChange}
                    required
                    disabled={!isEditing || isSubmitting}
                    variant="outlined"
                    size="small"
                    inputProps={{
                      sx: { py: 1.5 }
                    }}
                  />
                </Box>
              )}

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  {(formData.type ?? 'PAGE') === 'PAGE' ? 'Page *' : 'Component *'}
                </Typography>
                <TextField
                  fullWidth
                  name="component"
                  value={formData.component}
                  onChange={handleTextChange}
                  required
                  disabled={!isEditing || isSubmitting}
                  variant="outlined"
                  size="small"
                  inputProps={{
                    sx: { py: 1.5 }
                  }}
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Status
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    name="status"
                    value={formData.status ? 'true' : 'false'}
                    onChange={handleSelectChange}
                    required
                    disabled={!isEditing || isSubmitting}
                    displayEmpty
                    inputProps={{ 
                      sx: { py: 1.5 } 
                    }}
                  >
                    <MenuItem value="true">Active</MenuItem>
                    <MenuItem value="false">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Paper 
                variant="outlined" 
                sx={{ 
                  mt: 4, 
                  p: 2, 
                  borderRadius: 1, 
                  bgcolor: 'rgba(0, 0, 0, 0.02)',
                  border: '1px solid rgba(0, 0, 0, 0.1)'
                }}
              >
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Metadata
                </Typography>
                {formData.created_at && (
                  <Box display="flex" alignItems="center" gap={1}>
                    <Tooltip title="Creation Date">
                      <AccessTimeIcon fontSize="small" sx={{ color: '#666', fontSize: '1rem' }} />
                    </Tooltip>
                    <Typography variant="body2" sx={{ color: '#666', fontSize: '0.9rem' }}>
                      Created: {formatToLocalTime(formData.created_at)}
                    </Typography>
                  </Box>
                )}
                {formData.updated_at && (
                  <Box display="flex" alignItems="center" gap={1} sx={{ mt: 1 }}>
                    <Tooltip title="Last Updated">
                      <AccessTimeIcon fontSize="small" sx={{ color: '#666', fontSize: '1rem' }} />
                    </Tooltip>
                    <Typography variant="body2" sx={{ color: '#666', fontSize: '0.9rem' }}>
                      Updated: {formatToLocalTime(formData.updated_at)}
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Box>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <Box>
              <Typography variant="subtitle1" fontWeight={500} gutterBottom>
                Associated Roles
              </Typography>
              <Typography variant="body2" color="text.primary" paragraph>
                Configure which roles have access to this resource.
              </Typography>
              
              {!isEditing ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  {existingRoles.length > 0 ? (
                    existingRoles.map(role => (
                      <Chip
                        key={role._id as string}
                        label={roles.find(r => r._id === role._id)?.name || role.name}
                        variant="outlined"
                        sx={{ 
                          borderRadius: 1, 
                          py: 2.5,
                          px: 1,
                          border: '1px solid #e0e0e0',
                          backgroundColor: '#f5f5f5',
                          color: '#333',
                          fontSize: '0.85rem',
                        }}
                      />
                    ))
                  ) : (
                    <Typography variant="body2" color="text.primary">
                      No roles have been assigned to this resource.
                    </Typography>
                  )}
                </Box>
              ) : (
                <>
                  <Autocomplete
                    multiple
                    id="roles"
                    options={roles.map(role => role._id || '')}
                    getOptionLabel={(roleId) => roles.find(r => r._id === roleId)?.name || ''}
                    value={selectedRoles}
                    onChange={(_, newValue) => setSelectedRoles(newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Choose roles"
                        variant="outlined"
                        size="small"
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((roleId, index) => (
                        <Chip
                          label={roles.find(r => r._id === roleId)?.name}
                          {...getTagProps({ index })}
                          size="small"
                          sx={{ 
                            borderRadius: 1,
                          }}
                        />
                      ))
                    }
                  />
                </>
              )}
            </Box>
          </TabPanel>
        </Box>

        <Divider />
        
        {/* Footer with action buttons - fixed at bottom */}
        <Box 
          sx={{ 
            p: 2, 
            display: 'flex', 
            justifyContent: 'flex-end',
            backgroundColor: 'white',
            borderTop: '1px solid rgba(0, 0, 0, 0.12)',
            zIndex: 9,
            flexShrink: 0,
          }}
        >
          {isEditing && (
            <Tooltip title="Save your changes">
              <Button
                variant="contained"
                onClick={() => setOpenConfirm(true)}
                disabled={isSubmitting}
                startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                sx={{ 
                  textTransform: 'none', 
                  fontWeight: 'bold', 
                  bgcolor: '#0F2A52',
                  color: 'white',
                  py: 0.75,
                  px: 3
                }}
              >
                {isSubmitting ? 'Saving...' : 'Save Changes'}
              </Button>
            </Tooltip>
          )}
        </Box>

        <ConfirmationDialogMAIQ
          open={openConfirm}
          onClose={() => setOpenConfirm(false)}
          onConfirm={handleEditSubmit}
          message={tabValue === 1 ? 
            "Are you sure you want to save the changes to roles?" : 
            "Are you sure you want to save the changes to this resource?"
          }
        />
        <ConfirmationDialogMAIQ
          open={openDeleteConfirm}
          onClose={() => setOpenDeleteConfirm(false)}
          onConfirm={handleDeleteResource}
          message="Are you sure you want to delete this resource?"
        />
      </Paper>
    </ModalMAIQ>
  );
};

export default ResourceDetailsModal; 